import type { Frame, NormalizedAnchor, Point } from "./document";

export type Rect = Frame;

export type Matrix2D = {
  a: number;
  b: number;
  c: number;
  d: number;
  tx: number;
  ty: number;
};

export const IDENTITY_MATRIX: Readonly<Matrix2D> = {
  a: 1,
  b: 0,
  c: 0,
  d: 1,
  tx: 0,
  ty: 0,
};

export function translationMatrix(x: number, y: number): Matrix2D {
  return { ...IDENTITY_MATRIX, tx: x, ty: y };
}

export function frameMatrix(
  frame: Frame,
  rotationDegrees = 0,
  anchor: NormalizedAnchor = { x: 0.5, y: 0.5 },
): Matrix2D {
  const radians = (rotationDegrees * Math.PI) / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const pivotX = frame.width * anchor.x;
  const pivotY = frame.height * anchor.y;

  return {
    a: cosine,
    b: sine,
    c: -sine,
    d: cosine,
    tx: frame.x + pivotX - cosine * pivotX + sine * pivotY,
    ty: frame.y + pivotY - sine * pivotX - cosine * pivotY,
  };
}

export function multiplyMatrices(parent: Matrix2D, child: Matrix2D): Matrix2D {
  return {
    a: parent.a * child.a + parent.c * child.b,
    b: parent.b * child.a + parent.d * child.b,
    c: parent.a * child.c + parent.c * child.d,
    d: parent.b * child.c + parent.d * child.d,
    tx: parent.a * child.tx + parent.c * child.ty + parent.tx,
    ty: parent.b * child.tx + parent.d * child.ty + parent.ty,
  };
}

export function invertMatrix(matrix: Matrix2D): Matrix2D | null {
  const determinant = matrix.a * matrix.d - matrix.b * matrix.c;
  if (Math.abs(determinant) < Number.EPSILON) return null;
  const inverse = 1 / determinant;

  return {
    a: matrix.d * inverse,
    b: -matrix.b * inverse,
    c: -matrix.c * inverse,
    d: matrix.a * inverse,
    tx: (matrix.c * matrix.ty - matrix.d * matrix.tx) * inverse,
    ty: (matrix.b * matrix.tx - matrix.a * matrix.ty) * inverse,
  };
}

export function transformPoint(matrix: Matrix2D, point: Point): Point {
  return {
    x: matrix.a * point.x + matrix.c * point.y + matrix.tx,
    y: matrix.b * point.x + matrix.d * point.y + matrix.ty,
  };
}

export function rectFromPoints(points: readonly Point[]): Rect {
  if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  const xs = points.map(({ x }) => x);
  const ys = points.map(({ y }) => y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);
  return {
    x: left,
    y: top,
    width: Math.max(...xs) - left,
    height: Math.max(...ys) - top,
  };
}

export function transformRect(matrix: Matrix2D, rect: Rect): Rect {
  return rectFromPoints([
    transformPoint(matrix, { x: rect.x, y: rect.y }),
    transformPoint(matrix, { x: rect.x + rect.width, y: rect.y }),
    transformPoint(matrix, { x: rect.x + rect.width, y: rect.y + rect.height }),
    transformPoint(matrix, { x: rect.x, y: rect.y + rect.height }),
  ]);
}

export function unionRects(rects: readonly Rect[]): Rect | null {
  if (rects.length === 0) return null;
  const left = Math.min(...rects.map(({ x }) => x));
  const top = Math.min(...rects.map(({ y }) => y));
  const right = Math.max(...rects.map(({ x, width }) => x + width));
  const bottom = Math.max(...rects.map(({ y, height }) => y + height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function rectContainsPoint(rect: Rect, point: Point) {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  );
}

export function rectsIntersect(first: Rect, second: Rect) {
  return !(
    first.x + first.width < second.x ||
    second.x + second.width < first.x ||
    first.y + first.height < second.y ||
    second.y + second.height < first.y
  );
}

export function localFrameBounds(frame: Frame): Rect {
  return { x: 0, y: 0, width: frame.width, height: frame.height };
}
